import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, MessageCircle, Calendar, CreditCard, Mail, Webhook, Shield } from "lucide-react";

const INTEGRATIONS = [
  {
    id: "whatsapp",
    icon: MessageCircle,
    color: "from-emerald-500 to-emerald-700",
    name: "WhatsApp Business",
    desc: "Envia mensagens automáticas (lembrete, confirmação, pesquisa de satisfação) direto pelo seu WhatsApp.",
    how: [
      "1. Crie uma conta no WhatsApp Business API (Meta) ou contrate um BSP (Twilio, Z-API, Take Blip).",
      "2. Pegue o número WhatsApp aprovado + token de acesso (geralmente entregue pelo BSP em 24-72h).",
      "3. Clique em \"Conectar\" abaixo e cole o token. Pronto — automações enviam direto pelo seu número.",
      "💡 Custo: ~R$ 0,03 a 0,05 por mensagem template + R$ 0,40 por janela de conversa de 24h.",
    ],
    docs: "https://developers.facebook.com/docs/whatsapp/getting-started/",
    status: "Em breve · disparo via wa.me já funciona",
  },
  {
    id: "google-calendar",
    icon: Calendar,
    color: "from-sky-500 to-sky-700",
    name: "Google Calendar",
    desc: "Sincroniza atendimentos com a agenda Google de cada profissional. Evita conflito de horários.",
    how: [
      "1. Cada profissional faz \"Conectar com Google\" no seu próprio perfil.",
      "2. Autoriza o app a ler e escrever na agenda dele.",
      "3. Atendimentos criados no sistema aparecem automaticamente no Google Calendar do profissional, e bloqueios externos puxam pro nosso sistema.",
      "💡 OAuth bidirecional · gratuito.",
    ],
    docs: "https://developers.google.com/calendar/api/quickstart/python",
    status: "Em breve · Sprint OAuth",
  },
  {
    id: "stripe-link",
    icon: CreditCard,
    color: "from-purple-500 to-purple-700",
    name: "Link de Pagamento (Stripe)",
    desc: "Gera link de pagamento para enviar ao cliente (PIX, cartão, boleto) e atualiza o financeiro quando pago.",
    how: [
      "1. Crie conta Stripe Brasil (gratuita).",
      "2. Pegue a Secret Key em Painel Stripe → Developers → API keys.",
      "3. Cole abaixo. Cada venda criada pode gerar link de pagamento em 1 clique.",
      "💡 Taxa Stripe Brasil: ~3,49% + R$ 0,40 por transação cartão; PIX 0,99%.",
    ],
    docs: "https://stripe.com/docs/payments/payment-links",
    status: "Em breve · próxima sprint",
  },
  {
    id: "smtp-email",
    icon: Mail,
    color: "from-amber-500 to-amber-700",
    name: "E-mail (SMTP)",
    desc: "Envia notificações por email — mais barato e abrangente que WhatsApp pra avisos não-urgentes.",
    how: [
      "1. Em Configurações → Integrações, configure seu SMTP (Gmail, Outlook, SendGrid, Resend).",
      "2. Use \"Email\" como canal nas automações.",
      "💡 Já disponível! Veja em Configurações → Integrações.",
    ],
    docs: "/settings",
    status: "Disponível",
  },
  {
    id: "webhook",
    icon: Webhook,
    color: "from-slate-500 to-slate-700",
    name: "Webhook genérico (n8n, Zapier, Make)",
    desc: "Para integrar com qualquer outro sistema (CRM externo, ERP, planilha Google, etc).",
    how: [
      "1. No n8n/Zapier, crie um Webhook node e copie a URL gerada.",
      "2. Cole essa URL como ação \"webhook.post\" numa automação.",
      "3. A cada disparo, mandamos um JSON pro seu workflow.",
      "💡 Útil para puxar leads do Instagram Ads, mandar pro Google Sheets, etc.",
    ],
    docs: "https://docs.n8n.io/integrations/builtin/core-nodes/n8n-nodes-base.webhook/",
    status: "Disponível como ação custom",
  },
];

export default function IntegrationsCatalog() {
  return (
    <Card className="p-5 rounded-tenant space-y-4">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
          <Shield className="w-3 h-3" /> Catálogo de integrações
        </div>
        <h2 className="font-display text-xl font-bold mt-1">Plataformas que ligam com este sistema</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Use estas integrações nas suas automações. Clique em "Como configurar" pra ver o passo a passo.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {INTEGRATIONS.map((it) => {
          const Icon = it.icon;
          return (
            <details key={it.id} data-testid={`integration-${it.id}`} className="rounded-lg border overflow-hidden">
              <summary className="cursor-pointer list-none flex items-start gap-3 p-3 hover:bg-muted/40">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${it.color} text-white flex items-center justify-center shrink-0`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    {it.name}
                    <Badge variant={it.status.startsWith("Disponível") ? "default" : "outline"} className="text-[9px]">
                      {it.status.startsWith("Disponível") ? "Disponível" : "Em breve"}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{it.desc}</div>
                </div>
              </summary>
              <div className="px-3 pb-3 space-y-2 border-t bg-muted/20">
                <div className="text-xs text-muted-foreground pt-2">Status: <strong>{it.status}</strong></div>
                <div className="text-xs space-y-1.5 leading-relaxed">
                  {it.how.map((line, i) => <div key={i} className="font-medium text-foreground/90">{line}</div>)}
                </div>
                {it.docs && (
                  <a href={it.docs} target="_blank" rel="noopener noreferrer" className="text-xs underline inline-flex items-center gap-1 text-primary">
                    <ExternalLink className="w-3 h-3" /> Documentação oficial
                  </a>
                )}
              </div>
            </details>
          );
        })}
      </div>
    </Card>
  );
}
